import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Github, House } from "lucide-react";
import { GitHubOrgSelector } from "../GitHubOrgSelector";
import { BlacksmithWordmarkSVG } from "@repo/blacksmith-ui/blacksmith-wordmark";
import { BlacksmithLogomarkSVG } from "@repo/blacksmith-ui/blacksmith-logomark";
import { CodesmithLottieIcon } from "@/components/icons/CodesmithLottieIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { SandboxesIcon } from "@/components/sandboxes/SandboxesIcon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDashboardInstanceSlug } from "@/hooks/useDashboardNavigation";
import { useAuth } from "@/components/AuthProvider";
import { useHasHoverPointer, useIsCompactViewport } from "@/hooks/use-device";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { ShortcutKeys } from "@repo/blacksmith-ui/shortcut-keys";
import { cn } from "@repo/blacksmith-ui/cn";
import { AppHeaderBar } from "@/components/ui/app-header";
import { CollapsedIconTooltip, RAIL_ROW, RAIL_ROW_ACTIVE, RailRowContents } from "@repo/blacksmith-ui/app-rail";
import { SidebarAdminRow, SidebarGlobalFooter } from "./SidebarGlobalChrome";
import { SidebarBlockedOrgBanner, useSidebarGlobalBehaviors } from "./useSidebarGlobalBehaviors";
import { useAppRailHiddenOnMobile } from "./mobileRailVisibility";
import { useActiveApp, isHomePath, isStandaloneAccountPath, isAdminPath, settingsPagePath } from "./useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "./useCodesmithEnabledCache";
import { RAIL_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_WIDTH_CSS_VAR, RAIL_WIDTH_TRANSITION_MS, RAIL_TRANSITION_EASING } from "./railConstants";
import { RAIL_SHORTCUT_LABELS, railShortcutSection, type RailShortcutSection } from "./railShortcuts";

/**
 * Slim app-switcher rail pinned to the left edge of the app. Hosts the
 * Blacksmith logo, top-level app entries (Home, GitHub Actions, Codesmith,
 * Proof), and the global footer controls (support, settings, notifications,
 * org switcher).
 *
 * The rail is 48px wide by default; clicking the logo pins it open at 200px
 * (row labels, org selector, full wordmark) until the logo is clicked again —
 * it does not auto-collapse on hover-out or navigation. The expanded rail is
 * part of the page layout: the contextual sidebar and main content slide
 * over in lockstep with the panel's own width transition (see `layoutWidth`
 * below and the matching `left` transition on the contextual Sidebar), rather
 * than being covered by an overlay.
 */

// Master switch for the rail's whole-panel hover expansion. Off by default:
// the rail stays 48px and each row surfaces its own tooltip, while the
// vCPU/status widget expands as a standalone hover overlay (RailStatusHoverCard)
// rather than growing the whole rail. Flip to true to restore the original
// behavior where hovering expands the entire rail to reveal row labels. Typed
// as boolean so both code paths stay type-checked at the default value.
const RAIL_HOVER_EXPAND: boolean = false;

// Hover-intent dwell before expanding. The pointer must rest on the rail for
// this long before the wipe starts, so a quick pass-over (e.g. on the way to
// the main content) never kicks off a half-open-then-reverse animation. Close
// stays instant, so the rail still feels snappy.
const RAIL_OPEN_DELAY_MS = 200;

// The mutually-exclusive rail rows that can read as active.
type RailSection = "home" | "actions" | "codesmith" | "proof" | "sandboxes" | "usage" | "admin";

// Slide/wipe transition for the expand/collapse. easeOutQuint (see
// RAIL_TRANSITION_EASING): the panel starts moving immediately and
// decelerates smoothly into its final width (an ease-out reads far smoother
// here than an ease-in-out, which hesitates then snaps). Duration/easing are
// shared with the contextual Sidebar's `left` transition (railConstants.ts)
// so the panel, the pushed main content, and the fixed-position contextual
// sidebar all slide in lockstep rather than one visibly lagging the others.
//
// This is an inline style, not a Tailwind class: Tailwind's content scanner
// only recognizes class strings that appear literally in source, and this
// value is assembled from imported constants, so a `[transition:...]`
// arbitrary-value class built from it would never get a matching CSS rule
// generated — the rail would silently skip the transition and jump straight
// to its new width. That's invisible on pages with a contextual Sidebar
// (its own `left` transition carries the visible slide), but on Codesmith
// pages, which push a plain flex sibling (SessionSidebar) with nothing else
// animating, it read as no transition at all.
const RAIL_TRANSITION_STYLE = `width ${RAIL_WIDTH_TRANSITION_MS}ms ${RAIL_TRANSITION_EASING}`;

// Set on navigation; hover expansion is ignored while true so the panel does
// not pop back open over the section the user just clicked into. Cleared once
// the pointer leaves the rail (a fresh hover re-enables expansion). Lives at
// module scope because the rail can still unmount and remount within a session
// (its Suspense boundary in SidebarLayout) and the flag must survive that
// remount.
let suppressExpandUntilPointerLeaves = false;

// Clears the post-navigation suppression once the pointer is actually off the
// rail. React's onMouseLeave can't be relied on for this: clicking a row
// collapses the panel (200→48px) instantly under a stationary cursor, and a
// resize out from under the pointer doesn't dispatch mouseleave — so the flag
// would otherwise stay armed and swallow the next genuine hover. A
// document-level mousemove checks the live pointer position instead.
//
// The listener lives at module scope, like the flag it clears: it must stay
// attached across any rail remount, because a mousemove missed in an
// instance-bound listener's teardown/re-arm gap could let the
// pointer travel off the rail unseen and then re-enter with the first
// delivered event already targeting the panel — leaving the flag stuck and
// hover dead until the pointer exits and re-enters. The rail is resolved from
// the DOM at event time (live rail or its suspense skeleton), so the check
// works whichever instance is mounted. If the rail leaves the page entirely,
// the first mousemove finds no rail, clears the flag, and removes the
// listener, so nothing lingers.
let suppressClearListener: ((ev: MouseEvent) => void) | null = null;
function removeSuppressClearListener() {
  if (suppressClearListener) {
    document.removeEventListener("mousemove", suppressClearListener);
    suppressClearListener = null;
  }
}

// Persists the logo-click pin (see `expanded`) across that same remount.
// Read once as the initial state on mount and written back on every change,
// so when the rail comes back after unmounting it resumes exactly how it was
// left, rather than resetting to collapsed each time.
let pinnedExpanded = false;

// `timeStamp` of the last keydown into a text field (see railShortcutSection).
// Module scope, like pinnedExpanded, so it survives rail remounts.
let lastTextEntryAt = -Infinity;
function armSuppressUntilPointerLeaves() {
  suppressExpandUntilPointerLeaves = true;
  removeSuppressClearListener();
  const check = (ev: MouseEvent) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('[data-slot="app-switcher-rail"], [data-slot="app-switcher-rail-skeleton"]')) {
      return;
    }
    suppressExpandUntilPointerLeaves = false;
    removeSuppressClearListener();
  };
  suppressClearListener = check;
  document.addEventListener("mousemove", check);
}
export default function AppSwitcherRail() {
  const $ = _c(173);
  const router = useRouter();
  const activeApp = useActiveApp();
  const routeSection = isAdminPath(router.pathname) ? "admin" : router.pathname === "/[org]/usage" || router.pathname === "/usage" ? "usage" : isHomePath(router.pathname) ? "home" : isStandaloneAccountPath(router.pathname) ? null : activeApp === "codesmith" ? "codesmith" : activeApp === "proof" ? "proof" : activeApp === "sandboxes" ? "sandboxes" : "actions";
  const [optimisticSection, setOptimisticSection] = useState(null);
  const activeSection = optimisticSection ?? routeSection;
  const railRowNavigationRef = useRef(false);
  useIsCompactViewport();
  const hiddenOnMobile = useAppRailHiddenOnMobile(activeApp);
  const hasHoverPointer = useHasHoverPointer();
  const {
    codesmithAutomationsEnabled,
    orgsResolved,
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  const urlOrg = router.query.org;
  const {
    user
  } = useAuth();
  const urlInstanceSlug = useDashboardInstanceSlug(user?.default_instance_slug);
  let t0;
  if ($[0] !== activeNamedOrg?.orgName || $[1] !== orgsResolved || $[2] !== urlInstanceSlug || $[3] !== urlOrg) {
    t0 = (typeof urlOrg === "string" ? urlOrg : undefined) || activeNamedOrg?.orgName || (orgsResolved ? undefined : getCachedLastActiveOrg(urlInstanceSlug)?.orgName);
    $[0] = activeNamedOrg?.orgName;
    $[1] = orgsResolved;
    $[2] = urlInstanceSlug;
    $[3] = urlOrg;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const currentOrg = t0;
  const homeHref = currentOrg ? `/${currentOrg}/home` : "/home";
  const runsJobsHref = currentOrg ? `/${currentOrg}/runs/jobs` : "/runs";
  const sessionsNewHref = currentOrg ? `/${currentOrg}/sessions/new` : "/sessions/new";
  const proofHref = currentOrg ? `/${currentOrg}/proof` : "/proof";
  const sandboxesHref = currentOrg ? `/${currentOrg}/sandboxes` : "/sandboxes";
  const {
    activeGithubOrgBlocked: globalOrgBlocked,
    activeGithubOrgSuspendedInstallation,
    gPrefixActiveRef
  } = useSidebarGlobalBehaviors();
  const [expanded, setExpanded] = useState(pinnedExpanded);
  const slim = !expanded;
  const layoutWidth = expanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH;
  const [instantClose, setInstantClose] = useState(false);
  const [codesmithRowHovered, setCodesmithRowHovered] = useState(false);
  const panelRef = useRef(null);
  const openTimerRef = useRef(null);
  const portalMouseListenerRef = useRef(null);
  const portalObserverRef = useRef(null);
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
    };
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const clearOpenTimer = t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (portalMouseListenerRef.current) {
        document.removeEventListener("mousemove", portalMouseListenerRef.current);
        portalMouseListenerRef.current = null;
      }
      if (portalObserverRef.current) {
        portalObserverRef.current.disconnect();
        portalObserverRef.current = null;
      }
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const removePortalTracker = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      removePortalTracker();
      const checkMouse = ev => {
        const target = ev.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const overPanel = panelRef.current?.contains(target);
        const overPortal = target.closest("[data-radix-popper-content-wrapper]");
        const portalOpen = document.querySelector("[data-radix-popper-content-wrapper]");
        if (!overPanel && !overPortal && !portalOpen) {
          setExpanded(false);
          removePortalTracker();
        }
      };
      portalMouseListenerRef.current = checkMouse;
      document.addEventListener("mousemove", checkMouse);
      const observer = new MutationObserver(() => {
        if (document.querySelector("[data-radix-popper-content-wrapper]")) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          setExpanded(false);
        }
        removePortalTracker();
      });
      observer.observe(document.body, {
        childList: true
      });
      portalObserverRef.current = observer;
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const installPortalTracker = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = section => e => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      setOptimisticSection(section);
      railRowNavigationRef.current = true;
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleRailRowClick = t4;
  let t5;
  if ($[9] !== expanded || $[10] !== hasHoverPointer) {
    t5 = () => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
        return;
      }
      removePortalTracker();
      if (expanded) {
        return;
      }
      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        if (!hasHoverPointer || suppressExpandUntilPointerLeaves) {
          return;
        }
        if (!panelRef.current?.matches(":hover")) {
          return;
        }
        setExpanded(true);
      }, RAIL_OPEN_DELAY_MS);
    };
    $[9] = expanded;
    $[10] = hasHoverPointer;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (!RAIL_HOVER_EXPAND) {
        return;
      }
      clearOpenTimer();
      suppressExpandUntilPointerLeaves = false;
      removeSuppressClearListener();
      const related = e_0.relatedTarget;
      if (related instanceof HTMLElement && related.closest("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      if (document.querySelector("[data-radix-popper-content-wrapper]")) {
        installPortalTracker();
        return;
      }
      setExpanded(false);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleMouseLeave = t6;
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      removePortalTracker();
      clearOpenTimer();
    };
    t8 = [removePortalTracker, clearOpenTimer];
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== router.events) {
    t9 = () => {
      const handleRouteChangeStart = () => {
        const railRowNavigation = railRowNavigationRef.current;
        railRowNavigationRef.current = false;
        if (railRowNavigation && panelRef.current?.matches(":hover")) {
          armSuppressUntilPointerLeaves();
        }
        clearOpenTimer();
        removePortalTracker();
      };
      router.events.on("routeChangeStart", handleRouteChangeStart);
      return () => {
        router.events.off("routeChangeStart", handleRouteChangeStart);
      };
    };
    t10 = [router.events, removePortalTracker, clearOpenTimer];
    $[15] = router.events;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== router.events) {
    t11 = () => {
      const settle = () => {
        if (RAIL_HOVER_EXPAND) {
          setInstantClose(true);
          setExpanded(false);
        }
        setOptimisticSection(null);
      };
      router.events.on("routeChangeComplete", settle);
      router.events.on("routeChangeError", settle);
      return () => {
        router.events.off("routeChangeComplete", settle);
        router.events.off("routeChangeError", settle);
      };
    };
    t12 = [router.events];
    $[18] = router.events;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[21] !== instantClose) {
    t13 = () => {
      if (!instantClose) {
        return;
      }
      const id = requestAnimationFrame(() => setInstantClose(false));
      return () => cancelAnimationFrame(id);
    };
    t14 = [instantClose];
    $[21] = instantClose;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[24] !== expanded) {
    t15 = () => {
      pinnedExpanded = expanded;
    };
    t16 = [expanded];
    $[24] = expanded;
    $[25] = t15;
    $[26] = t16;
  } else {
    t15 = $[25];
    t16 = $[26];
  }
  useLayoutEffect(t15, t16);
  let t17;
  let t18;
  if ($[27] !== layoutWidth) {
    t17 = () => {
      document.documentElement.style.setProperty(RAIL_WIDTH_CSS_VAR, `${layoutWidth}px`);
    };
    t18 = [layoutWidth];
    $[27] = layoutWidth;
    $[28] = t17;
    $[29] = t18;
  } else {
    t17 = $[28];
    t18 = $[29];
  }
  useLayoutEffect(t17, t18);
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  useLayoutEffect(_temp2, t19);
  let t20;
  let t21;
  if ($[31] !== codesmithEnabled || $[32] !== router || $[33] !== sessionsNewHref) {
    t20 = () => {
      if (!codesmithEnabled) {
        return;
      }
      const handleNewChat = e_1 => {
        if (e_1.altKey && e_1.shiftKey && !e_1.metaKey && !e_1.ctrlKey && e_1.code === "KeyN") {
          e_1.preventDefault();
          setOptimisticSection("codesmith");
          router.push(sessionsNewHref);
        }
      };
      document.addEventListener("keydown", handleNewChat);
      return () => document.removeEventListener("keydown", handleNewChat);
    };
    t21 = [codesmithEnabled, sessionsNewHref, router];
    $[31] = codesmithEnabled;
    $[32] = router;
    $[33] = sessionsNewHref;
    $[34] = t20;
    $[35] = t21;
  } else {
    t20 = $[34];
    t21 = $[35];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[36] !== codesmithAutomationsEnabled || $[37] !== currentOrg || $[38] !== router) {
    t22 = () => {
      if (!codesmithAutomationsEnabled) {
        return;
      }
      const handleAutomations = e_2 => {
        if (e_2.altKey && e_2.shiftKey && !e_2.metaKey && !e_2.ctrlKey && e_2.code === "KeyA") {
          e_2.preventDefault();
          const automationsPath = currentOrg ? `/${currentOrg}/automations` : "/automations";
          setOptimisticSection("codesmith");
          router.push(automationsPath);
        }
      };
      document.addEventListener("keydown", handleAutomations);
      return () => document.removeEventListener("keydown", handleAutomations);
    };
    t23 = [codesmithAutomationsEnabled, currentOrg, router];
    $[36] = codesmithAutomationsEnabled;
    $[37] = currentOrg;
    $[38] = router;
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  useEffect(t22, t23);
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [];
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  useEffect(_temp4, t24);
  const railShortcutsActive = routeSection !== "codesmith" && optimisticSection !== "codesmith";
  let t25;
  let t26;
  if ($[42] !== codesmithEnabled || $[43] !== gPrefixActiveRef || $[44] !== homeHref || $[45] !== proofHref || $[46] !== railShortcutsActive || $[47] !== router || $[48] !== runsJobsHref || $[49] !== sandboxesHref || $[50] !== sessionsNewHref) {
    t25 = () => {
      if (!railShortcutsActive) {
        return;
      }
      const hrefs = {
        home: homeHref,
        actions: runsJobsHref,
        codesmith: codesmithEnabled ? sessionsNewHref : null,
        proof: codesmithEnabled ? proofHref : null,
        sandboxes: sandboxesHref
      };
      const handleRailShortcut = e_4 => {
        if (gPrefixActiveRef.current) {
          return;
        }
        const section_0 = railShortcutSection(e_4, lastTextEntryAt);
        const href = section_0 && hrefs[section_0];
        if (!section_0 || !href) {
          return;
        }
        e_4.preventDefault();
        setOptimisticSection(section_0);
        router.push(href);
      };
      document.addEventListener("keydown", handleRailShortcut);
      return () => document.removeEventListener("keydown", handleRailShortcut);
    };
    t26 = [railShortcutsActive, homeHref, runsJobsHref, codesmithEnabled, sessionsNewHref, proofHref, sandboxesHref, gPrefixActiveRef, router];
    $[42] = codesmithEnabled;
    $[43] = gPrefixActiveRef;
    $[44] = homeHref;
    $[45] = proofHref;
    $[46] = railShortcutsActive;
    $[47] = router;
    $[48] = runsJobsHref;
    $[49] = sandboxesHref;
    $[50] = sessionsNewHref;
    $[51] = t25;
    $[52] = t26;
  } else {
    t25 = $[51];
    t26 = $[52];
  }
  useEffect(t25, t26);
  const t27 = currentOrg ?? undefined;
  let t28;
  if ($[53] !== activeGithubOrgSuspendedInstallation || $[54] !== globalOrgBlocked || $[55] !== t27) {
    t28 = <SidebarBlockedOrgBanner activeGithubOrgBlocked={globalOrgBlocked} suspendedInstallation={activeGithubOrgSuspendedInstallation} currentOrg={t27} />;
    $[53] = activeGithubOrgSuspendedInstallation;
    $[54] = globalOrgBlocked;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  const t29 = expanded ? "expanded" : "collapsed";
  const t30 = hiddenOnMobile && "max-md:hidden";
  let t31;
  if ($[57] !== t30) {
    t31 = cn("group relative h-full shrink-0 flex flex-col overflow-hidden", "bg-sidebar shadow-[inset_-1px_0_0_0_var(--color-border)]", t30);
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  const t32 = instantClose ? "none" : RAIL_TRANSITION_STYLE;
  let t33;
  if ($[59] !== layoutWidth || $[60] !== t32) {
    t33 = {
      width: layoutWidth,
      transition: t32
    };
    $[59] = layoutWidth;
    $[60] = t32;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  const t34 = expanded ? "Collapse sidebar" : "Expand sidebar";
  let t35;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setExpanded(_temp5);
    $[62] = t35;
  } else {
    t35 = $[62];
  }
  const t36 = expanded ? "Collapse sidebar" : "Expand sidebar";
  const t37 = expanded ? "opacity-0" : "opacity-100";
  const t38 = instantClose ? "transition-none" : "[transition:opacity_100ms_ease-out]";
  let t39;
  if ($[63] !== t37 || $[64] !== t38) {
    t39 = cn("flex size-8 shrink-0 items-center justify-center", t37, t38);
    $[63] = t37;
    $[64] = t38;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  let t40;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="h-4 w-6 text-[#0c0c0de5] dark:text-blacksmith-yellow"><BlacksmithLogomarkSVG /></div>;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t39) {
    t41 = <div className={t39}>{t40}</div>;
    $[67] = t39;
    $[68] = t41;
  } else {
    t41 = $[68];
  }
  let t42;
  if ($[69] !== expanded || $[70] !== instantClose) {
    t42 = <div aria-hidden={true} className="pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2"><BlacksmithWordmarkSVG expanded={expanded} instant={instantClose} className="h-4 w-auto origin-left scale-[1.0485] text-[#0c0c0de5] dark:text-blacksmith-yellow" /></div>;
    $[69] = expanded;
    $[70] = instantClose;
    $[71] = t42;
  } else {
    t42 = $[71];
  }
  let t43;
  if ($[72] !== t36 || $[73] !== t41 || $[74] !== t42) {
    t43 = <button type="button" onClick={t35} aria-label={t36} className="relative flex h-8 w-full items-center hover:opacity-70 transition-opacity cursor-pointer">{t41}{t42}</button>;
    $[72] = t36;
    $[73] = t41;
    $[74] = t42;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  let t44;
  if ($[76] !== t34 || $[77] !== t43) {
    t44 = <AppHeaderBar><CollapsedIconTooltip label={t34} enabled={true}>{t43}</CollapsedIconTooltip></AppHeaderBar>;
    $[76] = t34;
    $[77] = t43;
    $[78] = t44;
  } else {
    t44 = $[78];
  }
  let t45;
  if ($[79] !== expanded) {
    t45 = <div className="pt-[calc(var(--spacing-app-header-gap)-0.5rem)]"><GitHubOrgSelector expanded={expanded} /></div>;
    $[79] = expanded;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  const t46 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.home : undefined;
  const t47 = currentOrg ? true : null;
  const t48 = !!currentOrg;
  let t49;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = handleRailRowClick("home");
    $[81] = t49;
  } else {
    t49 = $[81];
  }
  const t50 = activeSection === "home" ? "page" : undefined;
  const t51 = activeSection === "home" && RAIL_ROW_ACTIVE;
  let t52;
  if ($[82] !== t51) {
    t52 = cn(RAIL_ROW, t51);
    $[82] = t51;
    $[83] = t52;
  } else {
    t52 = $[83];
  }
  let t53;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <RailRowContents icon={<House className="size-[18px]" />} label="Home" />;
    $[84] = t53;
  } else {
    t53 = $[84];
  }
  let t54;
  if ($[85] !== homeHref || $[86] !== t47 || $[87] !== t48 || $[88] !== t50 || $[89] !== t52) {
    t54 = <RoutePrefetchLink href={homeHref} prefetch={t47} prefetchOnIntent={t48} onClick={t49} aria-current={t50} className={t52}>{t53}</RoutePrefetchLink>;
    $[85] = homeHref;
    $[86] = t47;
    $[87] = t48;
    $[88] = t50;
    $[89] = t52;
    $[90] = t54;
  } else {
    t54 = $[90];
  }
  let t55;
  if ($[91] !== slim || $[92] !== t46 || $[93] !== t54) {
    t55 = <CollapsedIconTooltip label="Home" enabled={slim} shortcut={t46}>{t54}</CollapsedIconTooltip>;
    $[91] = slim;
    $[92] = t46;
    $[93] = t54;
    $[94] = t55;
  } else {
    t55 = $[94];
  }
  const t56 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.actions : undefined;
  const t57 = currentOrg ? true : null;
  const t58 = !!currentOrg;
  let t59;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = handleRailRowClick("actions");
    $[95] = t59;
  } else {
    t59 = $[95];
  }
  const t60 = activeSection === "actions" ? "page" : undefined;
  const t61 = activeSection === "actions" && RAIL_ROW_ACTIVE;
  let t62;
  if ($[96] !== t61) {
    t62 = cn(RAIL_ROW, t61);
    $[96] = t61;
    $[97] = t62;
  } else {
    t62 = $[97];
  }
  let t63;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <RailRowContents icon={<Github className="size-[18px]" />} label="GitHub Actions" />;
    $[98] = t63;
  } else {
    t63 = $[98];
  }
  let t64;
  if ($[99] !== runsJobsHref || $[100] !== t57 || $[101] !== t58 || $[102] !== t60 || $[103] !== t62) {
    t64 = <RoutePrefetchLink href={runsJobsHref} prefetch={t57} prefetchOnIntent={t58} onClick={t59} aria-current={t60} className={t62}>{t63}</RoutePrefetchLink>;
    $[99] = runsJobsHref;
    $[100] = t57;
    $[101] = t58;
    $[102] = t60;
    $[103] = t62;
    $[104] = t64;
  } else {
    t64 = $[104];
  }
  let t65;
  if ($[105] !== slim || $[106] !== t56 || $[107] !== t64) {
    t65 = <CollapsedIconTooltip label="GitHub Actions" enabled={slim} shortcut={t56}>{t64}</CollapsedIconTooltip>;
    $[105] = slim;
    $[106] = t56;
    $[107] = t64;
    $[108] = t65;
  } else {
    t65 = $[108];
  }
  let t66;
  if ($[109] !== activeSection || $[110] !== codesmithEnabled || $[111] !== codesmithRowHovered || $[112] !== currentOrg || $[113] !== railShortcutsActive || $[114] !== sessionsNewHref || $[115] !== slim) {
    t66 = codesmithEnabled && (() => {
      const codesmithRow = <RoutePrefetchLink href={sessionsNewHref} prefetchOnIntent={!!currentOrg} onMouseEnter={() => setCodesmithRowHovered(true)} onMouseLeave={() => setCodesmithRowHovered(false)} onClick={handleRailRowClick("codesmith")} aria-current={activeSection === "codesmith" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "codesmith" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<CodesmithLottieIcon play={codesmithRowHovered} />} label="[code]smith" /></RoutePrefetchLink>;
      return <Tooltip><TooltipTrigger asChild={true}>{codesmithRow}</TooltipTrigger>{slim && <TooltipContent side="right" className="flex items-center gap-2"><span className="leading-none">[code]smith</span>{railShortcutsActive && <ShortcutKeys keys={[RAIL_SHORTCUT_LABELS.codesmith]} />}</TooltipContent>}</Tooltip>;
    })();
    $[109] = activeSection;
    $[110] = codesmithEnabled;
    $[111] = codesmithRowHovered;
    $[112] = currentOrg;
    $[113] = railShortcutsActive;
    $[114] = sessionsNewHref;
    $[115] = slim;
    $[116] = t66;
  } else {
    t66 = $[116];
  }
  let t67;
  if ($[117] !== activeSection || $[118] !== codesmithEnabled || $[119] !== currentOrg || $[120] !== proofHref || $[121] !== railShortcutsActive || $[122] !== slim) {
    t67 = codesmithEnabled && <CollapsedIconTooltip label="Proof" enabled={slim} shortcut={railShortcutsActive ? RAIL_SHORTCUT_LABELS.proof : undefined}><RoutePrefetchLink href={proofHref} prefetchOnIntent={!!currentOrg} onClick={handleRailRowClick("proof")} aria-current={activeSection === "proof" ? "page" : undefined} className={cn(RAIL_ROW, activeSection === "proof" && RAIL_ROW_ACTIVE)}><RailRowContents icon={<ProofIconBold className="size-[18px]" />} label="Proof" /></RoutePrefetchLink></CollapsedIconTooltip>;
    $[117] = activeSection;
    $[118] = codesmithEnabled;
    $[119] = currentOrg;
    $[120] = proofHref;
    $[121] = railShortcutsActive;
    $[122] = slim;
    $[123] = t67;
  } else {
    t67 = $[123];
  }
  const t68 = railShortcutsActive ? RAIL_SHORTCUT_LABELS.sandboxes : undefined;
  const t69 = !!currentOrg;
  let t70;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = handleRailRowClick("sandboxes");
    $[124] = t70;
  } else {
    t70 = $[124];
  }
  const t71 = activeSection === "sandboxes" ? "page" : undefined;
  const t72 = activeSection === "sandboxes" && RAIL_ROW_ACTIVE;
  let t73;
  if ($[125] !== t72) {
    t73 = cn(RAIL_ROW, t72);
    $[125] = t72;
    $[126] = t73;
  } else {
    t73 = $[126];
  }
  let t74;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <RailRowContents icon={<SandboxesIcon className="size-[18px]" />} label="Sandboxes" />;
    $[127] = t74;
  } else {
    t74 = $[127];
  }
  let t75;
  if ($[128] !== sandboxesHref || $[129] !== t69 || $[130] !== t71 || $[131] !== t73) {
    t75 = <RoutePrefetchLink href={sandboxesHref} prefetchOnIntent={t69} onClick={t70} aria-current={t71} className={t73}>{t74}</RoutePrefetchLink>;
    $[128] = sandboxesHref;
    $[129] = t69;
    $[130] = t71;
    $[131] = t73;
    $[132] = t75;
  } else {
    t75 = $[132];
  }
  let t76;
  if ($[133] !== slim || $[134] !== t68 || $[135] !== t75) {
    t76 = <CollapsedIconTooltip label="Sandboxes" enabled={slim} shortcut={t68}>{t75}</CollapsedIconTooltip>;
    $[133] = slim;
    $[134] = t68;
    $[135] = t75;
    $[136] = t76;
  } else {
    t76 = $[136];
  }
  let t77;
  if ($[137] !== t55 || $[138] !== t65 || $[139] !== t66 || $[140] !== t67 || $[141] !== t76) {
    t77 = <nav className="flex flex-col gap-1 pt-1" aria-label="Apps">{t55}{t65}{t66}{t67}{t76}</nav>;
    $[137] = t55;
    $[138] = t65;
    $[139] = t66;
    $[140] = t67;
    $[141] = t76;
    $[142] = t77;
  } else {
    t77 = $[142];
  }
  const t78 = activeSection === "admin";
  let t79;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = handleRailRowClick("admin");
    $[143] = t79;
  } else {
    t79 = $[143];
  }
  let t80;
  if ($[144] !== expanded || $[145] !== slim || $[146] !== t78) {
    t80 = <SidebarAdminRow expanded={expanded} active={t78} onClick={t79} hideLabelWhenCollapsed={slim} />;
    $[144] = expanded;
    $[145] = slim;
    $[146] = t78;
    $[147] = t80;
  } else {
    t80 = $[147];
  }
  let t81;
  if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <div className="flex-1" />;
    $[148] = t81;
  } else {
    t81 = $[148];
  }
  const t82 = currentOrg ?? undefined;
  const t83 = activeApp === "codesmith";
  let t84;
  if ($[149] !== currentOrg || $[150] !== t83) {
    t84 = settingsPagePath(currentOrg, t83);
    $[149] = currentOrg;
    $[150] = t83;
    $[151] = t84;
  } else {
    t84 = $[151];
  }
  let t85;
  if ($[152] !== activeSection || $[153] !== expanded || $[154] !== slim || $[155] !== t82 || $[156] !== t84) {
    t85 = <SidebarGlobalFooter expanded={expanded} currentOrg={t82} settingsHref={t84} activeSection={activeSection} onSectionClick={handleRailRowClick} className="pb-2" hideLabelWhenCollapsed={slim} statusAsHoverCard={slim} />;
    $[152] = activeSection;
    $[153] = expanded;
    $[154] = slim;
    $[155] = t82;
    $[156] = t84;
    $[157] = t85;
  } else {
    t85 = $[157];
  }
  let t86;
  if ($[158] !== t44 || $[159] !== t45 || $[160] !== t77 || $[161] !== t80 || $[162] !== t85) {
    t86 = <div className="flex h-full w-full flex-col px-2">{t44}{t45}{t77}{t80}{t81}{t85}</div>;
    $[158] = t44;
    $[159] = t45;
    $[160] = t77;
    $[161] = t80;
    $[162] = t85;
    $[163] = t86;
  } else {
    t86 = $[163];
  }
  let t87;
  if ($[164] !== handleMouseEnter || $[165] !== t29 || $[166] !== t31 || $[167] !== t33 || $[168] !== t86) {
    t87 = <div ref={panelRef} data-slot="app-switcher-rail" data-state={t29} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={t31} style={t33}>{t86}</div>;
    $[164] = handleMouseEnter;
    $[165] = t29;
    $[166] = t31;
    $[167] = t33;
    $[168] = t86;
    $[169] = t87;
  } else {
    t87 = $[169];
  }
  let t88;
  if ($[170] !== t28 || $[171] !== t87) {
    t88 = <TooltipProvider delayDuration={0}>{t28}{t87}</TooltipProvider>;
    $[170] = t28;
    $[171] = t87;
    $[172] = t88;
  } else {
    t88 = $[172];
  }
  return t88;
}
function _temp5(prev) {
  return !prev;
}
function _temp4() {
  const noteTextEntry = _temp3;
  document.addEventListener("keydown", noteTextEntry, true);
  return () => document.removeEventListener("keydown", noteTextEntry, true);
}
function _temp3(e_3) {
  if (isTypingTarget(e_3.target)) {
    lastTextEntryAt = e_3.timeStamp;
  }
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.documentElement.style.removeProperty(RAIL_WIDTH_CSS_VAR);
}